import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '../../components/public/Navbar';
import Footer from '../../components/public/Footer';
import { publicPosApi } from '../../services/api';

const fmt = (p) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(p);

/* ── Swipeable Image Slider ── */
function ImageGallery({ images, onClick }) {
  const [idx, setIdx] = useState(0);
  const timer = useRef(null);
  const touchStart = useRef(0);
  const imgs = (images || []).filter(Boolean);
  useEffect(() => {
    if (imgs.length <= 1) return;
    timer.current = setInterval(() => setIdx(p => (p + 1) % imgs.length), 3000);
    return () => clearInterval(timer.current);
  }, [imgs.length]);
  const go = (i, e) => { e.stopPropagation(); setIdx(i); clearInterval(timer.current); if (imgs.length > 1) timer.current = setInterval(() => setIdx(p => (p + 1) % imgs.length), 3000); };
  const ts = (e) => { touchStart.current = e.touches[0].clientX; };
  const te = (e) => { const d = touchStart.current - e.changedTouches[0].clientX; if (Math.abs(d) > 40) { clearInterval(timer.current); setIdx(p => d > 0 ? (p + 1) % imgs.length : (p - 1 + imgs.length) % imgs.length); timer.current = setInterval(() => setIdx(p => (p + 1) % imgs.length), 3000); } };
  if (!imgs.length) return null;
  return (
    <div className="relative overflow-hidden cursor-pointer group/img select-none" onClick={() => onClick && onClick(imgs, idx)} onTouchStart={ts} onTouchEnd={te}>
      <div className="aspect-[4/3] bg-gray-100 relative">
        {imgs.map((s, i) => <img key={i} src={s} alt="" className={`absolute inset-0 w-full h-full object-cover transition-transform duration-500 ${i === idx ? 'translate-x-0 opacity-100 z-10' : i < idx ? '-translate-x-full opacity-0 z-0' : 'translate-x-full opacity-0 z-0'}`} />)}
      </div>
      {imgs.length > 1 && (
        <>
          <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 flex gap-1.5 z-30">{imgs.map((_, i) => <button key={i} onClick={e => go(i, e)} className={`w-2 h-2 rounded-full transition-all ${i === idx ? 'bg-white scale-110 shadow' : 'bg-white/50'}`} />)}</div>
          <span className="absolute top-2.5 right-2.5 bg-black/50 text-white text-[10px] px-2 py-0.5 rounded-full z-30 font-medium">{imgs.length} foto</span>
        </>
      )}
    </div>
  );
}

/* ── Detail Bottom Sheet ── */
function DetailModal({ product, type, onClose, onAddToCart, openLightbox }) {
  const imgs = [product.image_url, ...(product.images || [])].filter(Boolean);
  const price = product.base_price ?? product.price ?? 0;
  const isPkg = type === 'package';
  return (
    <div className="fixed inset-0 z-[150] flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative bg-white w-full sm:max-w-lg sm:rounded-2xl shadow-2xl max-h-[85vh] flex flex-col rounded-t-2xl sm:rounded-2xl animate-slideUp" onClick={e => e.stopPropagation()}>
        <div className="sm:hidden flex justify-center pt-2 pb-1"><div className="w-10 h-1 bg-gray-300 rounded-full" /></div>
        <div className="flex-shrink-0 px-4 py-3 flex items-center justify-between border-b border-gray-100">
          <div className="flex items-center gap-2 min-w-0">
            {product.icon && <span className="text-2xl flex-shrink-0">{product.icon}</span>}
            <div className="min-w-0"><h2 className="font-bold text-gray-900 text-sm truncate">{product.name}</h2>{product.badge && <span className="inline-block mt-0.5 px-1.5 py-0.5 text-[10px] font-semibold bg-amber-100 text-amber-700 rounded-full">{product.badge}</span>}</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl flex-shrink-0 ml-2">&times;</button>
        </div>
        {imgs.length > 0 && <div className="flex-shrink-0"><ImageGallery images={imgs} onClick={(all, i) => openLightbox(all, i)} /></div>}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          <div className="flex items-center justify-between bg-gray-50 rounded-xl p-3">
            <div><p className="text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">{isPkg ? 'Harga Paket' : 'Harga'}</p><span className="text-xl font-extrabold text-navy">{fmt(price)}</span>{isPkg && product.tax_percent > 0 && <span className="text-[10px] text-gray-400 ml-1">+ppn {product.tax_percent}%</span>}{product.price_unit && <span className="text-[10px] text-gray-400 ml-1">{product.price_unit}</span>}</div>
            <button onClick={() => { onAddToCart(); onClose(); }} className="px-5 py-2.5 bg-brand text-white text-xs font-bold rounded-full hover:bg-brand-dark active:scale-95 transition-all shadow-md">+ Keranjang</button>
          </div>
          {product.short_description && <div><h3 className="text-xs font-semibold text-gray-700 mb-1">📝 Deskripsi</h3><p className="text-xs text-gray-600 leading-relaxed">{product.short_description}</p></div>}
          {/* Available Days & Time Range */}
          {(product.available_days && product.available_days.length > 0) || product.available_time_start || product.available_time_end ? (
            <div className="bg-brand/5 rounded-xl p-3 border border-brand/10">
              <h3 className="text-xs font-semibold text-gray-700 mb-2">📅 Jadwal Tersedia</h3>
              {product.available_days && product.available_days.length > 0 && (
                <div className="mb-2">
                  <p className="text-[10px] text-gray-400 mb-1">Hari:</p>
                  <div className="flex flex-wrap gap-1">
                    {product.available_days.map(day => (
                      <span key={day} className="px-2 py-0.5 bg-white border border-brand/20 rounded-full text-[10px] font-medium text-brand capitalize">{day}</span>
                    ))}
                  </div>
                </div>
              )}
              {(product.available_time_start || product.available_time_end) && (
                <div>
                  <p className="text-[10px] text-gray-400 mb-1">Jam:</p>
                  <span className="inline-block px-2.5 py-1 bg-white border border-brand/20 rounded-full text-[10px] font-semibold text-brand">
                    {product.available_time_start || '?'} — {product.available_time_end || '?'}
                  </span>
                </div>
              )}
            </div>
          ) : null}
          {product.description && <div><h3 className="text-xs font-semibold text-gray-700 mb-1">📋 Detail Lengkap</h3><div className="text-xs text-gray-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: product.description }} /></div>}
          {product.features && Array.isArray(product.features) && product.features.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-gray-700 mb-2">✅ Yang Anda Dapatkan</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {product.features.map((f, j) => (
                  <div key={j} className="flex items-start gap-1.5 p-2 rounded-lg bg-brand/5">
                    <svg className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    <span className="text-[11px] text-gray-700">{typeof f === 'string' ? f : f.label || f}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {product.faq && Array.isArray(product.faq) && product.faq.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-gray-700 mb-2">❓ FAQ</h3>
              <div className="space-y-1.5">
                {product.faq.map((item, j) => (
                  <details key={j} className="bg-gray-50 rounded-lg p-2.5 group/faq">
                    <summary className="text-[11px] font-semibold text-gray-700 cursor-pointer list-none flex items-center justify-between">
                      {item.q}
                      <svg className="w-3.5 h-3.5 text-gray-400 group-open/faq:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </summary>
                    <p className="text-[11px] text-gray-500 mt-1.5">{item.a}</p>
                  </details>
                ))}
              </div>
            </div>
          )}
          {isPkg && product.items && product.items.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-gray-700 mb-2">📦 Item dalam Paket</h3>
              <div className="space-y-1.5">
                {product.items.map((pi, j) => (
                  <div key={j} className="flex items-center justify-between bg-gray-50 rounded-lg p-2.5">
                    <div className="flex items-center gap-2">
                      <span className="text-base">{pi.icon || '📋'}</span>
                      <div>
                        <p className="text-xs font-medium text-gray-800">{pi.name}</p>
                        <p className="text-[10px] text-gray-400">Qty: {pi.pivot?.quantity || pi.quantity || 1}</p>
                      </div>
                    </div>
                    <span className="text-xs font-semibold text-navy">{fmt(pi.pivot?.override_price || pi.price || 0)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex-shrink-0 bg-white border-t px-4 py-3 flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 border-2 border-gray-200 rounded-xl text-gray-600 font-semibold text-xs hover:bg-gray-50 transition">Tutup</button>
          <button onClick={() => { onAddToCart(); onClose(); }} className="flex-1 py-2.5 bg-brand text-white rounded-xl font-bold text-xs hover:bg-brand-dark transition shadow-md">+ Keranjang</button>
        </div>
      </div>
    </div>
  );
}

/* ── Main ── */
export default function OrderPage() {
  const { code } = useParams();
  const referralCode = code || null;
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [packages, setPackages] = useState([]);
  const [cart, setCart] = useState([]);
  const [customer, setCustomer] = useState({ customer_name: '', customer_phone: '', customer_email: '', customer_address: '', notes: '', preferred_date: '', preferred_time: '' });
  const [dateError, setDateError] = useState('');
  const [selCat, setSelCat] = useState('');
  const [tab, setTab] = useState('packages');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [referralWorker, setReferralWorker] = useState(null);
  const [doneOrder, setDoneOrder] = useState(null);
  const [lb, setLb] = useState({ open: false, images: [], index: 0 });
  const [detail, setDetail] = useState(null);
  const [showCart, setShowCart] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // Load referral worker and catalog if code is provided
        if (referralCode) {
          try {
            const catalogRes = await publicPosApi.getWorkerCatalog(referralCode);
            if (catalogRes.data.success && catalogRes.data.data) {
              setCategories(catalogRes.data.data.categories || []);
              setItems(catalogRes.data.data.items || []);
              setPackages(catalogRes.data.data.packages || []);
              setReferralWorker(catalogRes.data.data.worker || {});
            }
          } catch (e) {
            console.error('Failed to load worker catalog:', e);
            // Fallback to default catalogs
            const [c, i, p] = await Promise.all([publicPosApi.getCategories({ per_page: 50, is_active: 1 }), publicPosApi.getItems({ per_page: 200, is_active: 1 }), publicPosApi.getPackages({ per_page: 50, is_active: 1 })]);
            setCategories(c.data.data?.data || c.data.data || []);
            setItems(i.data.data?.data || i.data.data || []);
            setPackages(p.data.data?.data || p.data.data || []);
          }
        } else {
          // Load default catalogs if no referral code
          const [c, i, p] = await Promise.all([publicPosApi.getCategories({ per_page: 50, is_active: 1 }), publicPosApi.getItems({ per_page: 200, is_active: 1 }), publicPosApi.getPackages({ per_page: 50, is_active: 1 })]);
          setCategories(c.data.data?.data || c.data.data || []);
          setItems(i.data.data?.data || i.data.data || []);
          setPackages(p.data.data?.data || p.data.data || []);
        }
      } catch (e) { console.error(e); } finally { setLoading(false); }
    })();
  }, [referralCode]);

  const filtered = selCat ? items.filter(i => i.category_id === parseInt(selCat)) : items;
  const inCart = (item, t) => cart.find(ci => ci.itemable_type === t && ci.itemable_id === item.id);
  const addToCart = (item, t = 'item') => setCart(prev => { const ex = prev.find(ci => ci.itemable_type === t && ci.itemable_id === item.id); if (ex) return prev.map(ci => ci.itemable_type === t && ci.itemable_id === item.id ? { ...ci, quantity: ci.quantity + 1 } : ci); return [...prev, { itemable_type: t, itemable_id: item.id, name: item.name, price: item.base_price || item.price || 0, quantity: 1 }]; });
  const updateQty = (idx, q) => { if (q < 1) return setCart(prev => prev.filter((_, i) => i !== idx)); setCart(prev => prev.map((ci, i) => i === idx ? { ...ci, quantity: q } : ci)); };
  const openLb = (imgs, idx = 0) => { if (!imgs || !imgs.length) return; setLb({ open: true, images: imgs, index: idx }); };
  const total = cart.reduce((s, ci) => s + ci.price * ci.quantity, 0);

  // Get today's date in YYYY-MM-DD for min attribute
  const todayStr = new Date().toISOString().split('T')[0];
  const currentHour = new Date().getHours();

  const isTodayBlocked = customer.preferred_date && customer.preferred_date === todayStr && currentHour >= 15;

  const handleDateChange = (value) => {
    setCustomer({...customer, preferred_date: value});
    setDateError('');
  };

  const handleOrder = async (e) => {
    e.preventDefault();
    if (!cart.length) return alert('Tambahkan item/paket terlebih dahulu');
    if (!customer.customer_name || !customer.customer_phone || !customer.customer_email || !customer.customer_address) return alert('Nama, telepon, email, dan alamat wajib diisi');
    if (isTodayBlocked) {
      alert('Pemesanan untuk hari ini sudah ditutup (operasional 10:00–15:00 WIB). Silakan pilih hari berikutnya.');
      return;
    }
    setSaving(true);
    try {
      const orderPayload = {
        ...customer,
        preferred_day: customer.preferred_date || null,
        preferred_time: customer.preferred_time || null,
        payment_method: 'transfer',
        referral_code: referralCode || null,
        items: cart.map(ci => ({
          itemable_type: ci.itemable_type === 'package' ? 'service_package' : 'service_item',
          itemable_id: ci.itemable_id,
          quantity: ci.quantity
        }))
      };
      const res = await publicPosApi.submitOrder(orderPayload);
      setDoneOrder(res.data.data);
      setDone(true);
      setCart([]);
    } catch (err) {
      alert(err.response?.data?.message || 'Gagal');
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (<div className="min-h-screen bg-gray-50"><Navbar /><div className="max-w-lg mx-auto px-4 py-16 text-center"><div className="bg-white rounded-3xl shadow-xl p-8"><div className="text-5xl mb-4">✅</div><h1 className="text-2xl font-bold text-gray-800 mb-2">Pesanan Berhasil!</h1><p className="text-gray-500 mb-4 text-sm">Terima kasih, {customer.customer_name}. Tim kami akan segera menghubungi Anda.</p>{doneOrder && <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left border"><p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Nomor Pesanan</p><p className="text-xl font-mono font-bold text-navy">{doneOrder.order_number}</p><p className="text-xs text-gray-500 mt-2">Total: <span className="font-bold text-gray-800">{fmt(doneOrder.total)}</span></p></div>}<div className="flex flex-col sm:flex-row gap-2 justify-center"><a href="/" className="px-5 py-2.5 bg-brand text-white rounded-full font-semibold text-sm hover:bg-brand-dark transition">🏠 Beranda</a><button onClick={() => setDone(false)} className="px-5 py-2.5 border-2 border-gray-200 rounded-full text-gray-700 font-semibold text-sm hover:bg-gray-50 transition">📋 Pesan Lagi</button></div></div></div><Footer /></div>);
  }

  /* ── PRODUCT CARD ── */
  const Card = ({ product, type }) => {
    const imgs = [product.image_url, ...(product.images || [])].filter(Boolean);
    const price = product.base_price ?? product.price ?? 0;
    const existing = inCart(product, type);
    return (
      <div className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 active:scale-[0.98] transition-transform duration-150">
        {imgs.length ? <ImageGallery images={imgs} onClick={openLb} /> : <div className="aspect-[4/3] bg-gradient-to-br from-navy/5 to-brand/10 flex items-center justify-center"><span className="text-5xl">{product.icon || '📦'}</span></div>}
        <div className="p-3">
          <h3 className="font-bold text-gray-900 text-[13px] leading-snug line-clamp-2 mb-1">{product.name}</h3>
          <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 mb-1.5">
            <span className="text-sm font-extrabold text-navy">{fmt(price)}</span>
            {product.badge && <span className="text-[10px] font-semibold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">{product.badge}</span>}
            {product.price_unit && <span className="text-[10px] text-gray-400">{product.price_unit}</span>}
            {type === 'package' && product.tax_percent > 0 && <span className="text-[10px] text-gray-400">+ppn{product.tax_percent}%</span>}
          </div>
          {product.short_description && <p className="text-[11px] text-gray-500 leading-relaxed line-clamp-2 mb-2">{product.short_description}</p>}
          <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-50">
            <button onClick={(e) => { e.stopPropagation(); setDetail({ product, type }); }} className="text-brand text-[11px] font-semibold hover:underline flex-shrink-0">Selengkapnya</button>
            {existing ? (
              <div className="flex items-center gap-1">
                <button onClick={() => updateQty(cart.indexOf(existing), existing.quantity - 1)} className="w-7 h-7 rounded-full bg-gray-100 text-gray-700 text-sm font-bold flex items-center justify-center active:bg-gray-200 transition">−</button>
                <span className="text-sm font-bold text-gray-800 w-5 text-center">{existing.quantity}</span>
                <button onClick={() => addToCart(product, type)} className="w-7 h-7 rounded-full bg-brand text-white text-sm font-bold flex items-center justify-center active:bg-brand-dark transition">+</button>
              </div>
            ) : (
              <button onClick={() => addToCart(product, type)} className="px-3 py-1.5 bg-brand text-white text-[11px] font-semibold rounded-full active:bg-brand-dark transition flex-shrink-0">+ Pesan</button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Mobile header */}
      <div className="sm:hidden bg-white border-b border-gray-100 px-4 py-3 sticky top-0 z-40">
        <h1 className="text-base font-bold text-gray-900">Pesan Layanan</h1>
        <p className="text-xs text-gray-400 mt-0.5">Pilih layanan & isi data diri</p>
      </div>

      {/* Desktop header */}
      <div className="hidden sm:block bg-gradient-to-r from-navy to-navy/90 text-white py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold">Pesan Layanan</h1>
          <p className="text-white/70 mt-2 text-sm">Pilih layanan, isi data, dan tim kami siap membantu Anda!</p>
        </div>
      </div>

      {/* Referral Worker Banner */}
      {referralWorker && (
        <div className="bg-gradient-to-r from-brand/10 to-brand/5 border-b border-brand/20 px-4 sm:px-6 lg:px-8 py-4">
          <div className="max-w-7xl mx-auto flex items-center gap-3 sm:gap-4">
            <div className="flex-shrink-0">
              {referralWorker.photo_url ? (
                <img src={referralWorker.photo_url} alt="" className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover border-2 border-brand/30" />
              ) : (
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-brand/20 text-brand rounded-full flex items-center justify-center font-bold text-lg">{referralWorker.full_name?.charAt(0)}</div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm text-brand font-semibold uppercase tracking-wider">Referral dari</p>
              <p className="text-sm sm:text-base font-bold text-gray-900">{referralWorker.full_name}</p>
              <p className="text-xs sm:text-sm text-gray-600">{referralWorker.position}{referralWorker.department ? ` • ${referralWorker.department}` : ''}</p>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-0 sm:px-6 lg:px-8 py-0 sm:py-8">
        {loading ? <div className="flex flex-col items-center justify-center py-20"><div className="w-10 h-10 border-4 border-brand border-t-transparent rounded-full animate-spin mb-3" /><p className="text-gray-400 text-sm">Memuat layanan...</p></div> : (
          <div className="lg:grid lg:grid-cols-3 lg:gap-6">
            
            {/* ── PRODUCTS ── */}
            <div className="lg:col-span-2">
              {/* Mobile tabs */}
              <div className="sm:hidden bg-white border-b border-gray-100 px-4 py-2.5 sticky top-[57px] z-30 flex items-center gap-2 overflow-x-auto no-scrollbar">
                <button onClick={() => setTab('packages')} className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all flex-shrink-0 ${tab === 'packages' ? 'bg-brand text-white shadow-sm' : 'bg-gray-100 text-gray-600'}`}>🎁 Paket</button>
                <button onClick={() => setTab('items')} className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all flex-shrink-0 ${tab === 'items' ? 'bg-brand text-white shadow-sm' : 'bg-gray-100 text-gray-600'}`}>�️ Layanan</button>
                {tab === 'items' && <select value={selCat} onChange={e => setSelCat(e.target.value)} className="border border-gray-200 rounded-full px-3 py-1.5 bg-gray-50 text-xs font-medium focus:ring-1 focus:ring-brand outline-none flex-shrink-0"><option value="">🔍 Semua</option>{categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}</select>}
              </div>
              {/* Desktop tabs */}
              <div className="hidden sm:block bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-6">
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={() => setTab('packages')} className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${tab === 'packages' ? 'bg-brand text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>🎁 Paket</button>
                  <button onClick={() => setTab('items')} className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${tab === 'items' ? 'bg-brand text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>�️ Layanan</button>
                  {tab === 'items' && <div className="ml-auto"><select value={selCat} onChange={e => setSelCat(e.target.value)} className="border border-gray-200 rounded-full px-4 py-2 bg-gray-50 text-sm font-medium focus:ring-1 focus:ring-brand outline-none"><option value="">🔍 Semua Kategori</option>{categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}</select></div>}
                </div>
              </div>
              {/* Grid */}
              <div className="px-4 sm:px-0 pb-4 sm:pb-0">
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {tab === 'packages' && packages.map(p => <Card key={p.id} product={p} type="package" />)}
                  {tab === 'items' && filtered.map(i => <Card key={i.id} product={i} type="item" />)}
                </div>
                {tab === 'packages' && !packages.length && <div className="text-center py-16 text-gray-400"><span className="text-5xl block mb-3">📦</span><p className="text-sm">Belum ada paket</p></div>}
                {tab === 'items' && !filtered.length && <div className="text-center py-16 text-gray-400"><span className="text-5xl block mb-3">�️</span><p className="text-sm">Tidak ada layanan</p></div>}
              </div>
            </div>

            {/* ── DESKTOP SIDEBAR ── */}
            <div className="hidden lg:block space-y-4">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <h2 className="font-bold text-gray-800 mb-4 flex items-center gap-2">🛒 Keranjang {cart.length > 0 && <span className="bg-brand text-white text-[10px] px-1.5 py-0.5 rounded-full">{cart.length}</span>}</h2>
                {!cart.length ? <p className="text-gray-400 text-sm text-center py-8">Pilih layanan untuk memulai</p> : (
                  <div className="space-y-2 mb-4 max-h-[220px] overflow-y-auto">
                    {cart.map((ci, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-gray-50 p-2.5 rounded-lg text-sm">
                        <p className="text-gray-800 font-medium truncate flex-1 mr-2">{ci.name}</p>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <button onClick={() => updateQty(idx, ci.quantity - 1)} className="w-6 h-6 rounded-full bg-white border text-gray-500 text-xs font-bold hover:bg-gray-100">−</button>
                          <span className="font-bold w-4 text-center">{ci.quantity}</span>
                          <button onClick={() => addToCart(cart.find(c => c.itemable_id === ci.itemable_id) || ci, ci.itemable_type)} className="w-6 h-6 rounded-full bg-white border text-gray-500 text-xs font-bold hover:bg-gray-100">+</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="border-t pt-3 flex justify-between items-center"><span className="text-sm text-gray-500">Total</span><span className="text-lg font-extrabold text-navy">{fmt(total)}</span></div>
              </div>
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <h2 className="font-bold text-gray-800 mb-4">📝 Data Diri</h2>
                <form onSubmit={handleOrder} className="space-y-3">
                  <input required value={customer.customer_name} onChange={e => setCustomer({ ...customer, customer_name: e.target.value })} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-brand outline-none transition" placeholder="Nama lengkap *" />
                  <input required value={customer.customer_phone} onChange={e => setCustomer({ ...customer, customer_phone: e.target.value })} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-brand outline-none transition" placeholder="No. telepon *" />
                  <input required type="email" value={customer.customer_email} onChange={e => setCustomer({ ...customer, customer_email: e.target.value })} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-brand outline-none transition" placeholder="Email *" />
                  <textarea required value={customer.customer_address} onChange={e => setCustomer({ ...customer, customer_address: e.target.value })} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-brand outline-none transition resize-none" rows={2} placeholder="Alamat *" />
                  <textarea value={customer.notes} onChange={e => setCustomer({ ...customer, notes: e.target.value })} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-brand outline-none transition resize-none" rows={2} placeholder="Catatan (opsional)" />
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">📅 Tanggal Pemesanan</label>
                    <input
                      type="date"
                      value={customer.preferred_date}
                      min={todayStr}
                      onChange={e => handleDateChange(e.target.value)}
                      className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-brand outline-none transition bg-white ${dateError ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
                    />
                    <p className="text-[10px] text-gray-400 mt-1">Operasional: Senin–Minggu, 10:00–15:00 WIB</p>
                  </div>
                  {isTodayBlocked && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-center">
                      <p className="text-sm text-red-600 font-semibold">⚠️ Tidak dapat memesan untuk hari ini</p>
                      <p className="text-xs text-red-500 mt-1">Jam operasional kami sudah berakhir pukul 15:00 WIB. Silakan pilih tanggal besok atau hari berikutnya.</p>
                    </div>
                  )}
                  <button type="submit" disabled={saving || !cart.length || isTodayBlocked} className="w-full py-3 bg-brand text-white rounded-xl font-bold hover:bg-brand-dark disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md active:scale-[0.98]">{saving ? '⏳ Mengirim...' : '✅ Pesan Sekarang'}</button>
                  <p className="text-center text-xs text-gray-400">Respon dalam 1×24 jam</p>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── MOBILE FLOATING BUTTON + BOTTOM SHEET ── */}
      {!loading && (
        <>
          <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 px-4 py-3 bg-white/95 backdrop-blur border-t border-gray-100">
            <button
              onClick={() => setShowCart(true)}
              className="w-full py-3.5 bg-brand text-white rounded-2xl font-bold text-sm shadow-lg shadow-brand/25 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
            >
              {cart.length > 0 ? (
                <>
                  <span className="bg-white/20 text-white text-xs font-bold px-2 py-0.5 rounded-full">{cart.length}</span>
                  <span>Lihat Pesanan</span>
                  <span className="opacity-80">· {fmt(total)}</span>
                </>
              ) : (
                <span>🛒 Lihat Pesanan</span>
              )}
            </button>
          </div>
          <div className="lg:hidden h-20" />

          {showCart && (
            <div className="lg:hidden fixed inset-0 z-[100]" onClick={() => setShowCart(false)}>
              <div className="absolute inset-0 bg-black/50" />
              <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl max-h-[85vh] flex flex-col animate-slideUp" onClick={e => e.stopPropagation()}>
                <div className="flex justify-center pt-2 pb-1"><div className="w-10 h-1 bg-gray-300 rounded-full" /></div>
                <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                  <h2 className="font-bold text-gray-900">🛒 Keranjang {cart.length > 0 && <span className="text-brand text-sm">({cart.length} item)</span>}</h2>
                  <button onClick={() => setShowCart(false)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
                </div>
                <div className="flex-1 overflow-y-auto px-5 py-4">
                  {!cart.length ? (
                    <p className="text-gray-400 text-sm text-center py-10">Belum ada item dipilih</p>
                  ) : (
                    <div className="space-y-2 mb-4">
                      {cart.map((ci, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-gray-50 p-3 rounded-xl">
                          <div className="min-w-0 flex-1 mr-3">
                            <p className="text-sm font-semibold text-gray-800 truncate">{ci.name}</p>
                            <p className="text-xs text-gray-400">{fmt(ci.price)}</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button onClick={() => updateQty(idx, ci.quantity - 1)} className="w-7 h-7 rounded-full bg-white border border-gray-200 text-gray-600 text-sm font-bold flex items-center justify-center active:bg-gray-100 transition">−</button>
                            <span className="text-sm font-bold w-5 text-center">{ci.quantity}</span>
                            <button onClick={() => addToCart(cart.find(c => c.itemable_id === ci.itemable_id) || ci, ci.itemable_type)} className="w-7 h-7 rounded-full bg-brand text-white text-sm font-bold flex items-center justify-center active:bg-brand-dark transition">+</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="border-t border-gray-200 pt-3 flex justify-between items-center mb-5">
                    <span className="text-sm text-gray-500">Total</span>
                    <span className="text-xl font-extrabold text-navy">{fmt(total)}</span>
                  </div>
                  <h3 className="font-bold text-gray-800 mb-3 text-sm">📝 Data Diri</h3>
                  <form onSubmit={(e) => { handleOrder(e); setShowCart(false); }} className="space-y-3">
                    <input required value={customer.customer_name} onChange={e => setCustomer({ ...customer, customer_name: e.target.value })} className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-brand outline-none transition" placeholder="Nama lengkap *" />
                    <input required value={customer.customer_phone} onChange={e => setCustomer({ ...customer, customer_phone: e.target.value })} className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-brand outline-none transition" placeholder="No. telepon *" />
                    <input required type="email" value={customer.customer_email} onChange={e => setCustomer({ ...customer, customer_email: e.target.value })} className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-brand outline-none transition" placeholder="Email *" />
                    <textarea required value={customer.customer_address} onChange={e => setCustomer({ ...customer, customer_address: e.target.value })} className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-brand outline-none transition resize-none" rows={2} placeholder="Alamat *" />
                    <textarea value={customer.notes} onChange={e => setCustomer({ ...customer, notes: e.target.value })} className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-brand outline-none transition resize-none" rows={2} placeholder="Catatan (opsional)" />
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1">📅 Tanggal Pemesanan</label>
                      <input
                        type="date"
                        value={customer.preferred_date}
                        min={todayStr}
                        onChange={e => handleDateChange(e.target.value)}
                        className={`w-full border rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-brand outline-none transition bg-white ${dateError ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
                      />
                      <p className="text-[10px] text-gray-400 mt-1">Operasional: Senin–Minggu, 10:00–15:00 WIB</p>
                    </div>
                    {isTodayBlocked && (
                      <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-center">
                        <p className="text-sm text-red-600 font-semibold">⚠️ Tidak dapat memesan untuk hari ini</p>
                        <p className="text-xs text-red-500 mt-1">Jam operasional kami sudah berakhir pukul 15:00 WIB. Silakan pilih tanggal besok atau hari berikutnya.</p>
                      </div>
                    )}
                    <button type="submit" disabled={saving || !cart.length || isTodayBlocked} className="w-full py-3.5 bg-brand text-white rounded-2xl font-bold text-base hover:bg-brand-dark disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-brand/25 active:scale-[0.98]">{saving ? '⏳ Mengirim...' : '✅ Pesan Sekarang'}</button>
                    <p className="text-center text-xs text-gray-400 pb-4">Respon dalam 1×24 jam</p>
                  </form>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {detail && <DetailModal product={detail.product} type={detail.type} onClose={() => setDetail(null)} onAddToCart={() => addToCart(detail.product, detail.type)} openLightbox={openLb} />}

      {/* Lightbox */}
      {lb.open && (
        <div className="fixed inset-0 bg-black/95 z-[200] flex items-center justify-center" onClick={() => setLb({ ...lb, open: false })}>
          <button onClick={() => setLb({ ...lb, open: false })} className="absolute top-4 right-4 text-white/70 hover:text-white text-2xl z-10">✕</button>
          <button onClick={e => { e.stopPropagation(); setLb(p => ({ ...p, index: p.index > 0 ? p.index - 1 : p.images.length - 1 })); }} className="absolute left-3 sm:left-6 text-white/70 hover:text-white text-4xl font-light z-10 select-none">‹</button>
          <button onClick={e => { e.stopPropagation(); setLb(p => ({ ...p, index: p.index < p.images.length - 1 ? p.index + 1 : 0 })); }} className="absolute right-3 sm:right-6 text-white/70 hover:text-white text-4xl font-light z-10 select-none">›</button>
          <div className="text-center px-4" onClick={e => e.stopPropagation()}>
            <img src={lb.images[lb.index]} alt="" className="max-h-[80vh] max-w-[90vw] object-contain rounded-2xl shadow-2xl" />
            {lb.images.length > 1 && <p className="text-white/60 text-xs mt-3 font-mono">{lb.index + 1}/{lb.images.length}</p>}
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
}